import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { loadScriptByURL, SITE_KEY, validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

// COMPONENTS
import Button from '../../components/Button';

// ICONS
import { ReactComponent as EyeOn } from '../../assets/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../assets/svg/eye-off.svg';

// TYPES
import { AuthenticationLoginCommand, AccountRegistrationCommand } from '../../api/models';

interface LoginFormTypes {
	emailAddress?: string;
	organisationIdentifier?: AccountRegistrationCommand['organisationIdentifier'];
	paymentPlanId?: AccountRegistrationCommand['paymentPlanId'];
	profileType?: AccountRegistrationCommand['profileType'];
	previousUrl?: string;
	returnUrl?: string;
}

export const LoginForm: React.FC<LoginFormTypes> = ({
	emailAddress,
	organisationIdentifier,
	paymentPlanId,
	profileType,
	previousUrl,
	returnUrl = '/dashboard'
}) => {
	let history = useHistory();
	const dispatch = useAppDispatch();
	const [loading, setLoading] = React.useState<boolean>(false);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const formRef = React.createRef<HTMLFormElement>();
	const [showPassword, setShowPassword] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (formRef.current) {
			formRef.current.addEventListener('click', setFormErrorMessage.bind(null, ''));
		}

		return () => {
			if (formRef.current) {
				formRef.current.removeEventListener('click', setFormErrorMessage.bind(null, ''));
			}
		};
	}, []);

	React.useEffect(() => {
		// load google reCAPTCHA script
		loadScriptByURL('recaptcha-key', function () {});
	}, []);

	const initialValues: AuthenticationLoginCommand = {
		userName: emailAddress || '',
		password: ''
	};

	const validationSchema = Yup.object({
		userName: Yup.string().required('Required'),
		password: Yup.string().required('Required')
	});

	const toggleShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const login = async (values: AuthenticationLoginCommand, token: string) => {
		const response = await apibridge.doLogin({
			userName: values.userName,
			password: values.password,
			captchaToken: token
		});

		setLoading(false);

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length ? validationErrorsToString(response.validationErrors) : response.message
			);
		} else if (response.result) {
			if (response.result.isTwoFa) {
				history.push({
					pathname: '/two-factor-authentication',
					state: {
						username: values.userName,
						token: response.result.twoFaToken,
						returnUrl,
						organisationIdentifier,
						paymentPlanId,
						profileType
					}
				});
			} else {
				dispatch(
					UserSlice.actions.update({
						childAwareId: response.result.childAwareId,
						email: response.result.email,
						firstName: response.result.firstName,
						lastName: response.result.lastName,
						loggedIn: true
					})
				);

				history.push({
					pathname: returnUrl ? returnUrl.split('?')[0] : '/dashboard',
					search: returnUrl ? returnUrl.split('?')[1] : '/dashboard',
					state: {
						organisationIdentifier: organisationIdentifier,
						paymentPlanId: paymentPlanId,
						profileType: profileType
					}
				});
			}
		}
	};

	const doLogin = async (values: AuthenticationLoginCommand) => {
		setLoading(true);

		await window.grecaptcha.ready(() => {
			window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token: string) => {
				login(values, token);
			});
		});
	};

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={doLogin}>
			{(formik) => {
				return (
					<Form translate="yes" ref={formRef}>
						<div className="content">
							<div className="form-group">
								<label htmlFor="userName">ChildAware ID or Email</label>

								<Field name="userName" id="userName" type="text" className="form-control" />

								<ErrorMessage name="userName" component="div" className="form-validation-error" />
							</div>

							<div className="form-group">
								<label htmlFor="password">Password</label>

								<div className="password-wrapper">
									<Field
										name="password"
										id="password"
										type={showPassword ? 'text' : 'password'}
										className="form-control"
									/>

									<Button
										className="btn-text"
										buttonIcon={showPassword ? <EyeOff /> : <EyeOn />}
										onClick={toggleShowPassword}
									></Button>
								</div>

								<ErrorMessage name="password" component="div" className="form-validation-error" />
							</div>

							<p className="forgotten-links">
								Forgot your <Link to="/forgot-password">password</Link>
							</p>
						</div>

						<div className="actions">
							{!!formErrorMessage && <div className="form-validation-error">{formErrorMessage}</div>}

							<Button type="submit" className="btn-primary full-width" disabled={loading}>
								Log in
							</Button>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default LoginForm;
